'use client';

import { ProviderCategory } from '@repo/api-gw-sdk';
import { useMemo, type PropsWithChildren } from 'react';

import { SourceAccountsContext } from '@/contexts/sourceAccountsContext';

import { useDAL } from './useDAL';
import { useFeatureFlags } from './useFeatureFlags';

export const SourceAccountsProvider = ({ children }: PropsWithChildren) => {
  const dal = useDAL();
  const { GoogleWorkspaceSupport } = useFeatureFlags();

  // The server returns only cloud-platform accounts when no providerCategory
  // filter is sent, so SaaS accounts must be requested explicitly.
  const { data: sourceAccountsBody, mutate: mutateAccounts } =
    dal.cloudAccounts.source.useList(
      0,
      10000,
      GoogleWorkspaceSupport
        ? {
            filters: {
              providerCategory: {
                _in: [ProviderCategory.CloudPlatform, ProviderCategory.Saas],
              },
            },
          }
        : undefined
    );

  const sourceAccounts = useMemo(
    () =>
      sourceAccountsBody?.accounts
        ?.map((x) => ({
          ...x,
          name: x.name || x.providerAccountId,
        }))
        .sort(
          (a, b) =>
            a.sourceAccountAttributes.cloudProvider.localeCompare(
              b.sourceAccountAttributes.cloudProvider
            ) || a.name.localeCompare(b.name)
        ),
    [sourceAccountsBody]
  );

  return (
    <SourceAccountsContext.Provider
      value={{
        sourceAccounts,
        recalculateAccounts: () => void mutateAccounts(),
      }}
    >
      {children}
    </SourceAccountsContext.Provider>
  );
};
